import React from "react"
import { useNavigate } from "react-router-dom"
import Button from "./Button"
import { sentryService } from "../services/sentry"

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo)
    sentryService.captureException(error)
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} resetError={() => this.setState({ hasError: false, error: null })} />
    }

    return this.props.children
  }
}

function ErrorFallback({ error, resetError }) {
  const navigate = useNavigate()

  const handleGoHome = () => {
    resetError()
    navigate("/")
  }

  const handleGoBack = () => {
    resetError()
    navigate(-1)
  }

  return (
    <div className="flex min-h-screen flex-col justify-between bg-background">
      <div className="mx-auto w-full max-w-5xl p-12">
        <img className="mb-16 w-[300px]" src="/logo.png" alt="Encircled Logo" />

        <div className="flex flex-col items-center justify-center text-center">
          <div className="mb-8">
            <h1 className="mb-4 text-6xl font-bold text-brand">Oops!</h1>
            <h2 className="mb-2 text-2xl font-bold text-black">Something Went Wrong</h2>
            <p className="mb-4 text-lg text-gray-600">
              {error?.message || "An unexpected error occurred. Please try again."}
            </p>

            {import.meta.env.DEV && error?.stack && (
              <details className="mt-6 max-w-2xl text-left">
                <summary className="cursor-pointer text-sm font-medium text-gray-700">
                  Technical Details (Development Only)
                </summary>
                <pre className="mt-2 overflow-auto rounded-md bg-gray-100 p-4 text-xs text-gray-800">
                  {error.stack}
                </pre>
              </details>
            )}
          </div>

          <div className="flex gap-4">
            <Button onClick={handleGoBack} variant="secondary-outline">
              Go Back
            </Button>
            <Button onClick={handleGoHome}>
              Go Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ErrorBoundary
